import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  padding: '11px 14px',
  color: '#F0F4FF',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  boxSizing: 'border-box',
  fontFamily: 'Inter, sans-serif',
};

export default function ContactModal({ isOpen, onClose }) {
  const [status, setStatus] = useState('idle');
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.from_name,
          from_email: formData.from_email,
          subject: formData.subject,
          message: formData.message,
        },
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      setStatus('success');
      setFormData({ from_name: '', from_email: '', subject: '', message: '' });
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  const handleClose = () => {
    setStatus('idle');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(8px)',
              zIndex: 10000,
            }}
          />

          {/* Modal wrapper: flex centres the panel; framer-motion only animates scale/opacity */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              padding: '16px',
              pointerEvents: 'none',
            }}
          >
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            style={{
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'rgba(8,12,20,0.98)',
              border: '1px solid rgba(0,212,255,0.25)',
              borderRadius: '20px',
              padding: '36px',
              boxShadow: '0 0 60px rgba(0,212,255,0.12), 0 25px 50px rgba(0,0,0,0.8)',
              pointerEvents: 'auto',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '6px',
                cursor: 'pointer',
                color: '#8892A4',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <X size={18} />
            </button>

            {/* Success state */}
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ textAlign: 'center', padding: '20px 0' }}
              >
                <CheckCircle size={56} color="#39FF14" style={{ margin: '0 auto 16px' }} />
                <h3 style={{ color: '#F0F4FF', fontSize: '22px', fontWeight: 700, marginBottom: '8px', fontFamily: 'Syne, sans-serif' }}>
                  Message Sent!
                </h3>
                <p style={{ color: '#8892A4', fontSize: '15px', marginBottom: '24px', fontFamily: 'Inter, sans-serif' }}>
                  Nihanth will get back to you soon. In the meantime, connect on LinkedIn!
                </p>
                <a
                  href="https://linkedin.com/in/nihanthnaidu007"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '10px 24px',
                    background: 'rgba(0,212,255,0.08)',
                    border: '1px solid rgba(0,212,255,0.35)',
                    borderRadius: '10px',
                    color: '#00D4FF',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: 'DM Mono, monospace',
                  }}
                >
                  Connect on LinkedIn →
                </a>
              </motion.div>
            ) : (
              <>
                {/* Header */}
                <div style={{ marginBottom: '28px' }}>
                  <p style={{
                    color: '#00D4FF',
                    fontSize: '11px',
                    fontFamily: 'DM Mono, monospace',
                    marginBottom: '6px',
                    letterSpacing: '2px',
                  }}>
                    // GET IN TOUCH
                  </p>
                  <h2 style={{
                    color: '#F0F4FF',
                    fontSize: '26px',
                    fontWeight: 800,
                    margin: 0,
                    fontFamily: 'Syne, sans-serif',
                  }}>
                    Send a Message
                  </h2>
                  <p style={{ color: '#8892A4', fontSize: '14px', marginTop: '6px', fontFamily: 'Inter, sans-serif' }}>
                    Got an opportunity or just want to talk AI? Hit send.
                  </p>
                </div>

                {/* Error banner */}
                {status === 'error' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(255,80,80,0.08)',
                    border: '1px solid rgba(255,80,80,0.25)',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                  }}>
                    <AlertCircle size={16} color="#ff5050" />
                    <span style={{ color: '#ff5050', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                      Something went wrong. Email nihanthnaidu.kalisetti@my.liu.edu directly.
                    </span>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Name + Email row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        color: '#4A5A6A',
                        fontSize: '11px',
                        marginBottom: '6px',
                        letterSpacing: '1.5px',
                        fontFamily: 'DM Mono, monospace',
                      }}>
                        YOUR NAME *
                      </label>
                      <input
                        type="text"
                        name="from_name"
                        value={formData.from_name}
                        onChange={handleChange}
                        required
                        placeholder="John Smith"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        color: '#4A5A6A',
                        fontSize: '11px',
                        marginBottom: '6px',
                        letterSpacing: '1.5px',
                        fontFamily: 'DM Mono, monospace',
                      }}>
                        YOUR EMAIL *
                      </label>
                      <input
                        type="email"
                        name="from_email"
                        value={formData.from_email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label style={{
                      display: 'block',
                      color: '#4A5A6A',
                      fontSize: '11px',
                      marginBottom: '6px',
                      letterSpacing: '1.5px',
                      fontFamily: 'DM Mono, monospace',
                    }}>
                      SUBJECT *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="AI Engineer Opportunity at [Company]"
                      style={inputStyle}
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label style={{
                      display: 'block',
                      color: '#4A5A6A',
                      fontSize: '11px',
                      marginBottom: '6px',
                      letterSpacing: '1.5px',
                      fontFamily: 'DM Mono, monospace',
                    }}>
                      MESSAGE *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell me about the role, the team, or just say hi..."
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '14px 28px',
                      background: status === 'sending'
                        ? 'rgba(0,212,255,0.06)'
                        : '#00D4FF',
                      border: status === 'sending'
                        ? '1px solid rgba(0,212,255,0.3)'
                        : '1px solid #00D4FF',
                      borderRadius: '12px',
                      color: status === 'sending' ? '#00D4FF' : '#080C14',
                      fontSize: '15px',
                      fontWeight: 700,
                      cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      letterSpacing: '0.5px',
                      fontFamily: 'Syne, sans-serif',
                    }}
                  >
                    {status === 'sending' ? (
                      <>
                        <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
